import { useTranslation } from 'react-i18next';
import { formatDate, todayIso } from '@platform/core/lib/format';
import { isDoneStatus } from '@platform/engine/views/doneStatuses';
import { useAssignmentsQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { valueOf } from '../valueOf';
import { advisoryHealth, advisoryRecommendations, checklistItems } from './advisory';
import type { IAdvisoryPanelProps } from './IAdvisoryPanelProps';

const HEALTH_TONE = { healthy: '#1f8a5b', attention: '#b7791f', risk: '#c2410c' } as const;

/**
 * Панель «Поради» (UIUX forms/side-panels, прототип v4): «Рекомендовані дії» зі станом (У нормі · Потребує уваги · Під ризиком) і
 * кнопками дій, «Порадитися з ШІ»; «Контрольний список» із поступом — 7 пунктів позначаються з даних запису («авто»), решта — вручну.
 */
export function AdvisoryPanel({ rt, record, editor, onAction, onAskAi }: IAdvisoryPanelProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const { data: assignments = [] } = useAssignmentsQuery(record.id);
  const due = valueOf(record, {}, 'due');
  const input = {
    hasAssigneeField: rt.fields.some((f) => f.key === 'assignee'), assigned: Boolean(valueOf(record, {}, 'assignee')),
    hasDueField: rt.fields.some((f) => f.key === 'due'), due: typeof due === 'string' ? due : null, done: isDoneStatus(record.status),
    assignments: assignments.length, assignmentsDone: assignments.filter((a) => a.status === 'Done').length,
    links: record.counts?.links ?? 0, files: record.counts?.files ?? 0, comments: record.counts?.comments ?? 0,
    described: Boolean(String(valueOf(record, {}, 'description') ?? '').trim()), today: todayIso(),
  };
  const recs = advisoryRecommendations(input);
  const health = advisoryHealth(recs.length);
  const manual = (record.ext?.['checklist'] ?? {}) as Record<string, boolean>;
  const items = checklistItems(input, manual);
  const done = items.filter((i) => i.on).length;
  const canEdit = record.permissions?.edit !== false;
  const k = (key: string) => `recordPage.advisoryPanel.${key}`;
  return (
    <div className="rec-adv">
      <section className="rec-adv__card">
        <div className="rec-adv__head"><span className="rec-adv__title">{t(k('recommended'))}</span><span className="rec-adv__health" style={{ color: HEALTH_TONE[health] }}>{t(k(health))}</span></div>
        {recs.map((r) => (
          <div key={r.key} className="rec-adv__rec">
            <span className="rec-adv__dot" style={{ background: r.tone }} />
            <div className="rec-adv__text"><strong>{t(k(r.key), { date: r.date ? formatDate(r.date, lang) : '' })}</strong><span>{t(k(`${r.key}Text`))}</span></div>
            {r.action && canEdit && <button type="button" className="rec-adv__act" onClick={() => onAction(r.action!)}>{t(k(r.action))}</button>}
          </div>
        ))}
        {!recs.length && <div className="rec-adv__none">{t(k('nothing'))}</div>}
        <button type="button" className="rec-adv__ai" onClick={onAskAi}>{t(k('askAi'))}</button>
      </section>
      <section className="rec-adv__card">
        <div className="rec-adv__head"><span className="rec-adv__title">{t(k('checklist'))}</span><span className="rec-adv__sum">{t(k('checkSummary'), { done, total: items.length })}</span></div>
        <span className="rtab-meter"><span style={{ width: `${Math.round((done / items.length) * 100)}%` }} /></span>
        {items.map((i) => (
          <label key={i.key} className="rec-adv__check">
            <input type="checkbox" checked={i.on} disabled={!canEdit} onChange={() => void editor.patchNow({}, undefined, { checklist: { ...manual, [i.key]: !manual[i.key] } })} />
            <span>{t(k(i.key))}</span>
            {i.auto && <span className="rec-adv__auto">{t(k('auto'))}</span>}
          </label>
        ))}
      </section>
    </div>
  );
}
